import type { Dispatch, SetStateAction } from 'react';

import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { useTurnstile } from 'react-turnstile';

import { notifications } from '@mantine/notifications';

import { useAppDispatch } from '@xver/hooks/useAppDispatch';
import { Endpoints } from '@xver/apis';
import { requestAuthUser } from '@xver/store/auth/auth.actions';
import { set2FAData, setPhoneVerificationData } from '@xver/store/authPage/authPage.actions';

import { IconMoodPuzzled } from '@tabler/icons-react';
import storage from '@xver/utils/storage';
import type { PageType } from '../../../constants';
import { PAGE_TYPE } from '../../../constants';

interface useSignInProps {
    setPageType: Dispatch<SetStateAction<PageType>>;
}

export function useSignIn({ setPageType }: useSignInProps) {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const turnstile = useTurnstile();
    const { t } = useTranslation('notifications');

    const {
        formState: { isSubmitting },
        handleSubmit,
        register,
        setValue,
        reset,
    } = useForm({
        defaultValues: {
            email: '',
            password: '',
            turnstileToken: '',
        },
    });

    const onSubmit = handleSubmit(async (values) => {
        try {
            const res = await Endpoints.v1.Authentication.signIn(values);

            if (res.data?.token) {
                storage.set('_zugang', res.data.token);

                await dispatch(requestAuthUser());

                navigate('/dashboard');
                return;
            } else if (res.data) {
                if (res.data?.phoneNumber) {
                    toast.info(res.message);
                    dispatch(
                        setPhoneVerificationData({
                            phoneNumber: res.data.phoneNumber!,
                            verificationToken: res.data.verificationToken!,
                        })
                    );
                    setPageType(PAGE_TYPE.PHONE_NUMBER_VERIFICATION);
                    return;
                }

                if (res.data?.requires2FA) {
                    dispatch(
                        set2FAData({
                            email: values.email,
                            password: values.password,
                        })
                    );
                    setPageType(PAGE_TYPE.TWO_FACTOR_VERIFICATION);
                    return;
                }
            }

            reset();
        } catch ({ message }: any) {
            notifications.show({
                title: t('auth.signInFailed'),
                message: message || t('auth.signInFailedMessage'),
                icon: <IconMoodPuzzled size={32} color={'#eb5757'} />,
            });
            turnstile.reset();
            setValue('turnstileToken', '');
        }
    });

    return {
        isSubmitting,
        register,
        setValue,
        onSubmit,
    };
}
